import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-sm text-slate-600">
          Value: <span className="font-mono font-semibold">{payload[0].value?.toFixed(3)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

export const WaterfallChart = ({ data, title = "Causal Impact Waterfall" }) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-slate-500">
            Run a calculation to see the causal breakdown
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data for waterfall visualization
  const chartData = data.map((item, index) => {
    const prevValue = index > 0 ? data[index - 1].cumulative : 0;
    return {
      name: item.name,
      value: item.cumulative,
      start: prevValue,
      change: item.cumulative - prevValue,
      isFinal: item.final,
    };
  });

  const getBarColor = (entry) => {
    if (entry.isFinal) return '#1e3a5f'; // Navy for final
    if (entry.change < 0) return '#ef4444'; // Red for negative
    return '#10b981'; // Green for positive
  };

  return (
    <Card data-testid="waterfall-chart">
      <CardHeader>
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
        <p className="text-sm text-slate-500">
          Visualizing the step-by-step impact from LEI improvement to final KPI delta
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                dy={10}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => `${value.toFixed(2)}%`}
                label={{
                  value: 'Impact (%)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: '#64748b', fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${entry.name || index}`} fill={getBarColor(entry)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500" />
            <span className="text-slate-600">Positive Impact</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-rose-500" />
            <span className="text-slate-600">Dampening</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-navy-700" />
            <span className="text-slate-600">Final Result</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WaterfallChart;
